<script>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { frontendURL } from '../../../helper/URLHelper';
import { useUserRole } from 'dashboard/composables/useUserRole';

export default {
  props: {
    to: { type: String, default: '' },
    label: { type: String, default: '' },
    inboxId: { type: Number, default: 0 },
    labelColor: { type: String, default: '' },
    shouldTruncate: { type: Boolean, default: false },
    maxCharacterLength: { type: Number, default: 17 },
    icon: { type: String, default: '' },
    warningIcon: { type: String, default: '' },
    showChildCount: { type: Boolean, default: false },
    childItemCount: { type: Number, default: 0 },
    inboxSectionBtn: { type: Boolean, default: false },
    // Updated props to receive processed metadata from parent
    inboxMetaData: {
      type: Object,
      default: () => ({
        connectionStatus: '',
        displayText: 'Clique para conectar',
        metadata: null,
        integration_type: '',
        phone_number: '',
        hasAI: false,
      }),
    },
    isLoading: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' },
  },
  emits: ['inboxModal', 'editInboxModal', 'reconnectModal', 'connectInbox'],
  setup(props, { emit }) {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const { isAdmin, isManager } = useUserRole();
    const canEditInbox = computed(() => isAdmin.value || isManager.value);
    const canManageInboxConnection = computed(() => isAdmin.value);
    const shouldOpenRestrictedView = computed(() => !canEditInbox.value);

    const displayName = computed(() => {
      const customName = store.getters['inboxMetadata/getInboxDisplayName'](props.inboxId);
      return customName || props.label;
    });

    const menuTitle = computed(() => displayName.value);

    const truncatedLabel = computed(() => {
      const nameToShow = displayName.value;
      if (!props.shouldTruncate || nameToShow.length <= props.maxCharacterLength) return nameToShow;
      return `${nameToShow.substring(0, props.maxCharacterLength)}...`;
    });

    // Check if this label is currently active in the route
    const isLabelActive = computed(() => {
      // Only check for labels (not inbox items)
      if (props.labelColor && route.params.label) {
        return route.params.label === props.label;
      }
      return false;
    });

    // --- Normalization fix ---
    // Ensure only 0, 1 or 2 are returned; everything else becomes null (default UI)
    const connectionStatus = computed(() => {
      const raw = props.inboxMetaData?.connectionStatus;
      if (raw === 0 || raw === 1 || raw === 2) return raw;
      if (typeof raw === 'string') {
        const s = raw.trim();
        if (s === '0' || s === '1' || s === '2') return Number(s);
        const n = Number(s);
        if ([0, 1, 2].includes(n) && s === String(n)) return n; // accept clean numeric strings only
      }
      return null; // default/unknown
    });

    // Convenience flags to avoid triple-negations in template
    const isConnected = computed(() => connectionStatus.value === 2);
    const isDisconnected = computed(() => connectionStatus.value === 0);
    const isPending = computed(() => connectionStatus.value === 1);
    const isDefaultUI = computed(() => ![0, 1, 2].includes(connectionStatus.value));

    const integration_type = computed(() => props.inboxMetaData?.integration_type ?? '');
    const phone_number = computed(() => props.inboxMetaData?.phone_number ?? '');
    const integrationType = computed(() => props.inboxMetaData?.integration_type ?? '');
    const hasAI = computed(
      () =>
        props.inboxMetaData?.hasAI === true ||
        props.inboxMetaData?.metadata?.hasAI === true
    );

    const iconWrapperClasses = computed(() => '');

    const blockStatusClass = computed(() => {
      if (isConnected.value && hasAI.value) {
        return 'inbox-block-connected-ai';
      }
      return '';
    });

    const hasGradientBackground = computed(() => !!blockStatusClass.value);
    const gradientTextClass = computed(() =>
      hasGradientBackground.value
        ? 'text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.25)]'
        : ''
    );
    const aiHoverClass = computed(() =>
      isConnected.value && hasAI.value ? 'ai-hover-effect' : ''
    );

    const metaDisplayText = computed(() => {
      if (props.isLoading) return 'Carregando...';
      if (props.errorMessage) return props.errorMessage;
      if (isConnected.value) return props.inboxMetaData.displayText;
      if (isDisconnected.value) return 'Desconectado';
      if (isPending.value) return 'Aguardando conexão';
      return shouldOpenRestrictedView.value
        ? 'Não configurado'
        : 'Clique para conectar'; // default for ANY non 0/1/2 value
    });

    const isCountZero = computed(() => props.childItemCount === 0);
    const isRestrictedActionDisabled = computed(
      () => shouldOpenRestrictedView.value && !isConnected.value
    );
    const isConnectionActionDisabled = computed(
      () => !canManageInboxConnection.value && !isConnected.value
    );

    const openRestrictedInboxView = () => {
      emit('editInboxModal', props.label, props.inboxId, {
        integration_type: integration_type.value,
        display_phone_number: phone_number.value,
        connectionStatus: connectionStatus.value,
        viewOnly: true,
      });
    };

    const handleConnectClick = () => {
      if (isRestrictedActionDisabled.value || isConnectionActionDisabled.value) {
        return;
      }

      if (shouldOpenRestrictedView.value) {
        openRestrictedInboxView();
      } else if (isConnected.value) {
        emit('editInboxModal', props.label, props.inboxId, {
          integration_type: integration_type.value,
          display_phone_number: phone_number.value,
          connectionStatus: connectionStatus.value,
          viewOnly: false,
        });
      } else if (isDisconnected.value || isPending.value) {
        emit('reconnectModal', props.label, props.inboxId, {
          integration_type: integration_type.value,
          phone_number: phone_number.value,
          connectionStatus: connectionStatus.value,
        });
      } else {
        // default/unknown state -> open connect flow
        emit('inboxModal', props.label, props.inboxId);
      }
    };

    // Handle label click with toggle functionality
    const handleLabelClick = (navigate, e) => {
      // Only apply toggle logic for labels, not inboxes
      if (props.labelColor) {
        if (isLabelActive.value) {
          // If the label is already active, navigate to home (remove filter)
          e.preventDefault();
          const accountId = route.params.accountId;
          router.push(frontendURL(`accounts/${accountId}/dashboard`));
        } else {
          // Otherwise, use normal navigation
          navigate(e);
        }
      } else {
        // For non-label items, use normal navigation
        navigate(e);
      }
    };

    return {
      // state
      menuTitle,
      truncatedLabel,
      connectionStatus,
      hasAI,
      integration_type,
      phone_number,
      displayName,
      metaDisplayText,
      isCountZero,
      integrationType,
      isLabelActive,
      iconWrapperClasses,
      blockStatusClass,
      hasGradientBackground,
      gradientTextClass,
      aiHoverClass,
      // flags
      isConnected,
      isDisconnected,
      isPending,
      isDefaultUI,
      // user roles
      canEditInbox,
      canManageInboxConnection,
      shouldOpenRestrictedView,
      isRestrictedActionDisabled,
      isConnectionActionDisabled,
      // actions
      handleConnectClick,
      handleLabelClick,
    };
  },
};
</script>

<template>
  <router-link v-slot="{ href, isActive, navigate }" :to="to" custom active-class="active">
    <li
      class="mb-2 flex items-center px-2 rounded-md"
      :class="[
        {
          // Active highlighting for labels - use isLabelActive for labels, isActive for others
          'bg-woot-25 dark:bg-slate-800': (labelColor ? isLabelActive : isActive) && !(inboxSectionBtn && !isDefaultUI),

          // Background by status (fallback when no gradient)
          'bg-grass-100 dark:bg-grass-800/70': inboxSectionBtn && isConnected && !blockStatusClass,
          'bg-ruby-300 dark:bg-ruby-500': inboxSectionBtn && isDisconnected,
          'bg-amber-100 dark:bg-amber-800': inboxSectionBtn && isPending,
          // DEFAULT: gray background when NOT 0/1/2
          'bg-slate-50 dark:bg-slate-800': inboxSectionBtn && isDefaultUI,

          // Hovers
          'hover:bg-slate-25 hover:text-bg-50 dark:hover:bg-slate-800': !(inboxSectionBtn && !isDefaultUI),
          'hover:bg-grass-75 dark:hover:bg-grass-800/90': inboxSectionBtn && isConnected,
          'hover:bg-ruby-400 dark:hover:bg-ruby-500/80': inboxSectionBtn && isDisconnected,
          'hover:bg-amber-200 dark:hover:bg-amber-800/80': inboxSectionBtn && isPending,
          // DEFAULT hover
          'hover:bg-slate-50 dark:hover:bg-slate-800': inboxSectionBtn && isDefaultUI,

          'text-ellipsis overflow-hidden whitespace-nowrap max-w-full': shouldTruncate,
          'h-[6.75rem]': inboxSectionBtn,
          'h-7': !inboxSectionBtn,
          // Slight dim for default
          'opacity-80': inboxSectionBtn && isDefaultUI,
          'relative overflow-hidden': !!blockStatusClass,
        },
        blockStatusClass,
        aiHoverClass,
      ]"
      @click="handleLabelClick(navigate, $event)"
    >
      <span
        v-if="isConnected && hasAI"
        class="absolute inset-0 shimmer-effect ai-hover-shimmer pointer-events-none"
      ></span>
      <a :href="href" class="inline-flex flex-col text-left max-w-full w-full gap-1.5">
        <!-- First row with icon and inbox name -->
        <div>
          <div class="flex items-center justify-between w-full">
            <div class="flex items-center">
              <!-- Instagram icon -->
              <div
                v-if="integrationType === 'instagram'"
                class="relative flex items-center mr-1 status-icon"
                v-tooltip.top="'Instagram'"
              >
                <span
                  class="text-xl relative z-10"
                  :class="[
                    hasGradientBackground
                      ? 'text-white'
                      : 'text-slate-700 dark:text-slate-100',
                    'i-ri-instagram-fill',
                  ]"
                ></span>
              </div>

              <!-- WhatsApp icon for both official and evolution integrations -->
              <div
                v-if="integrationType === 'official' || integrationType === 'evolution'"
                class="relative flex items-center mr-1 status-icon"
                v-tooltip.top="integrationType === 'official' ? 'WhatsApp Oficial' : 'WhatsApp Não Oficial'"
              >
                <span
                  class="text-xl relative z-10"
                  :class="[
                    hasGradientBackground
                      ? 'text-white'
                      : 'text-slate-700 dark:text-slate-100',
                    'i-ri-whatsapp-fill',
                  ]"
                ></span>
                <span v-if="integrationType === 'official'" class="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full flex items-center justify-center bg-white z-20">
                  <span class="i-lucide-check text-woot-600 dark:text-woot-900 text-xxs"></span>
                </span>
              </div>

              <!-- Messenger icon -->
              <div
                v-if="integrationType === 'messenger'"
                class="relative flex items-center mr-1 status-icon"
                v-tooltip.top="'Messenger'"
              >
                <span
                  class="text-xl relative z-10"
                  :class="[
                    hasGradientBackground
                      ? 'text-white'
                      : 'text-slate-700 dark:text-slate-100',
                    'i-ri-messenger-fill',
                  ]"
                ></span>
              </div>

              <span
                v-if="labelColor"
                class="inline-flex rounded-full bg-slate-100 h-3 w-3 mr-1.5 border border-slate-50 dark:border-slate-900"
                :style="{ backgroundColor: labelColor }"
              />

              <span
                :title="menuTitle"
                class="text-sm"
                :class="[
                  {
                    'text-woot-500 dark:text-woot-400': labelColor && isLabelActive,
                    'text-grass-700 dark:text-grass-400': !labelColor && isActive && isConnected,
                    'text-slate-300 dark:text-slate-500': inboxSectionBtn && isDefaultUI,
                    'dark:text-slate-800': isPending,
                    'text-slate-700 dark:text-white': isDisconnected,
                    'text-ellipsis overflow-hidden whitespace-nowrap max-w-full': shouldTruncate,
                  },
                  gradientTextClass,
                  !hasGradientBackground ? 'text-slate-700 dark:text-slate-100' : '',
                ]"
              >
                {{ truncatedLabel }}
              </span>
            </div>

            <span
              v-if="showChildCount"
              class="bg-slate-50 dark:bg-slate-700 rounded-full min-w-[18px] justify-center items-center flex text-xxs mx-1 py-0 px-1 ml-auto"
              :class="isCountZero ? 'text-slate-300 dark:text-slate-500' : 'text-slate-700 dark:text-slate-50'"
            >
              {{ childItemCount }}
            </span>

            <span v-if="warningIcon" class="inline-flex ml-1 bg-red-50 dark:bg-red-900 p-0.5 rounded-sm">
              <fluent-icon v-tooltip.top-end="$t('SIDEBAR.REAUTHORIZE')" class="text-xxs text-red-500 dark:text-red-300" :icon="warningIcon" size="12" />
            </span>

            <!-- AI Icon -->
            <span
              v-if="inboxSectionBtn && hasAI"
              v-tooltip.top="'IA Ativada'"
              class="inline-flex ml-auto p-1 rounded-md"
              :class="[
                hasGradientBackground
                  ? 'bg-white/20'
                  : 'bg-fuchsia-100 dark:bg-fuchsia-900/50'
              ]"
            >
              <span
                class="i-lucide-sparkles text-sm"
                :class="[
                  hasGradientBackground
                    ? 'text-white'
                    : 'text-fuchsia-600 dark:text-fuchsia-400'
                ]"
              ></span>
            </span>
          </div>

          <!-- Second row with status -->
          <div v-if="inboxSectionBtn" class="flex items-center mt-1">
            <span
              class="text-sm truncate max-w-full"
              :class="[
                {
                  'text-grass-700 dark:text-slate-100': isConnected,
                  'text-slate-500 dark:text-slate-400': isDefaultUI,
                  'text-slate-700 dark:text-white': isDisconnected,
                  'text-amber-800 dark:text-white': isPending,
                },
                gradientTextClass,
                !hasGradientBackground ? '' : 'text-white',
              ]"
            >
              {{ metaDisplayText }}
            </span>
          </div>
        </div>

        <!-- Button at the bottom of the inbox section -->
        <div v-if="inboxSectionBtn" class="mt-2">
          <button
            type="button"
            class="w-full text-sm py-1.5 px-2 rounded text-center font-medium transition-all duration-200 ease-in-out"
            :disabled="isRestrictedActionDisabled || isConnectionActionDisabled"
            :class="{
              'bg-fuchsia-600 text-white hover:bg-fuchsia-500 focus-visible:ring-2 focus-visible:ring-fuchsia-200 dark:bg-fuchsia-600/90 dark:hover:bg-fuchsia-500': isConnected && hasAI,
              'bg-grass-600 text-white hover:bg-grass-500 dark:bg-grass-700 dark:hover:bg-grass-600': isConnected && !hasAI,
              'bg-ruby-500 text-white hover:bg-ruby-600 dark:bg-ruby-600/90 dark:hover:bg-ruby-500': isDisconnected,
              'bg-amber-400 text-white hover:bg-amber-500 dark:bg-amber-600 dark:hover:bg-amber-800': isPending,
              // DEFAULT: blue button when NOT 0/1/2
              'bg-woot-500 text-white hover:enabled:brightness-110 focus-visible:brightness-110 dark:bg-woot-600 dark:hover:bg-woot-700': isDefaultUI,
              'cursor-not-allowed opacity-60 hover:bg-ruby-500 dark:hover:bg-ruby-600/90': (isRestrictedActionDisabled || isConnectionActionDisabled) && isDisconnected,
              'cursor-not-allowed opacity-60 hover:bg-amber-400 dark:hover:bg-amber-600': (isRestrictedActionDisabled || isConnectionActionDisabled) && isPending,
              'cursor-not-allowed opacity-60 hover:bg-woot-500 dark:hover:bg-woot-600': (isRestrictedActionDisabled || isConnectionActionDisabled) && isDefaultUI,
            }"
            @click.stop.prevent="handleConnectClick"
          >
            {{
              isRestrictedActionDisabled || isConnectionActionDisabled
                ? 'INDISPONÍVEL'
                : shouldOpenRestrictedView
                  ? 'VISUALIZAR'
                  : isConnected
                    ? 'EDITAR'
                    : 'CONECTAR'
            }}
          </button>
        </div>
      </a>
    </li>
  </router-link>
</template>

<style scoped>
.status-icon {
  display: inline-flex;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 9999px;
  overflow: hidden;
  justify-content: center;
  align-items: center;
}

.inbox-connected {
  background: linear-gradient(135deg, #6366f1, #818cf8) !important;
  box-shadow: 0 0 8px rgba(99, 102, 241, 0.5);
}

.inbox-connected-ai {
  background: linear-gradient(135deg, #8b5cf6, #6366f1, #3b82f6) !important;
  animation: pulse-glow 2s ease-in-out infinite;
}

.inbox-block-connected {
  background: linear-gradient(135deg, #6366f1, #818cf8) !important;
  box-shadow: 0 0 12px rgba(99, 102, 241, 0.35);
}

.inbox-block-connected-ai {
  background: linear-gradient(135deg, #8b5cf6, #6366f1, #3b82f6) !important;
}

.shimmer-effect {
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.5) 50%,
    transparent 100%
  );
  animation: shimmer 1.5s infinite;
  opacity: 0;
}

.ai-hover-effect:hover .ai-hover-shimmer {
  opacity: 1;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

</style>
